import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabaseClient } from '../../lib/supabase';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { logger } from '../../lib/logger';
import { HOTEL_ID } from '../../lib/supabase/configClient';
import { SocialIcon } from './SocialIcon';

interface SocialNetworksModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: any;
  translations: Record<string, Record<string, string>>;
}

interface SocialNetwork {
  id: string;
  platform: string;
  url: string;
  order_index: number;
  is_active: boolean;
}

export const SocialNetworksModal: React.FC<SocialNetworksModalProps> = ({
  isOpen,
  onClose,
  config,
  translations
}) => {
  const { i18n } = useTranslation();
  const [socialNetworks, setSocialNetworks] = useState<SocialNetwork[]>([]);

  // Fetch social networks from Supabase
  const { data, isLoading, error } = useQuery({
    queryKey: ['social-networks', HOTEL_ID],
    queryFn: async () => {
      try {
        logger.debug('Fetching social networks', {
          category: 'API',
          data: { hotelId: HOTEL_ID }
        });

        const { data, error } = await supabaseClient
          .from('social_networks')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('is_active', true)
          .order('order_index');

        if (error) {
          throw error;
        }

        logger.debug('Social networks fetched successfully', {
          category: 'API',
          data: { count: data.length }
        });

        return data as SocialNetwork[];
      } catch (error) {
        logger.error('Error fetching social networks', {
          category: 'API',
          data: { error, hotelId: HOTEL_ID }
        });
        throw error;
      }
    },
    enabled: isOpen,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000 // 30 minutes
  });

  useEffect(() => {
    if (data) {
      setSocialNetworks(data);
    }
  }, [data]);

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      analyticsService.trackEvent({
        event_type: 'interaction',
        event_name: 'social_networks_modal_close',
        event_data: {
          close_method: 'overlay'
        }
      });
      onClose();
    }
  };

  const handleCloseClick = () => {
    analyticsService.trackEvent({
      event_type: 'interaction',
      event_name: 'social_networks_modal_close',
      event_data: {
        close_method: 'button'
      }
    });
    onClose();
  };

  const handleSocialClick = (network: SocialNetwork) => {
    analyticsService.trackEvent({
      event_type: 'interaction',
      event_name: 'social_network_click',
      event_data: {
        platform: network.platform,
        url: network.url
      }
    });
    window.open(network.url, '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[400] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white w-full max-w-md relative rounded-lg shadow-lg"
        style={{ borderRadius: config.border_radius }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleCloseClick}
          className="absolute top-0 right-0 w-12 h-12 bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-opacity flex items-center justify-center"
          style={{ 
            borderTopRightRadius: config.border_radius,
            borderBottomLeftRadius: config.border_radius
          }}
        >
          <X size={20} />
        </button>

        {/* Content */}
        <div className="p-6">
          <h2 
            className="text-xl font-semibold mb-6"
            style={{ color: config['dark-color'] }}
          >
            {getTranslatedText('social_networks') || 'Social Networks'}
          </h2>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-4">
              {getTranslatedText('error_loading') || 'Error loading social networks'}
            </div>
          ) : socialNetworks.length === 0 ? (
            <div className="text-gray-500 text-center py-4">
              {getTranslatedText('no_social_networks') || 'No social networks available'}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {socialNetworks.map(network => (
                <button
                  key={network.id}
                  onClick={() => handleSocialClick(network)}
                  className="flex flex-col items-center justify-center p-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <SocialIcon 
                    platform={network.platform} 
                    size={40} 
                    color={config['major-color']} 
                  />
                  <span className="mt-2 text-sm font-medium text-center">
                    {network.platform}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};